import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Calendar, User, TrendingUp, FileDown } from "lucide-react";
import { toast } from "sonner";
import { MealPlanTabs } from "@/components/mealplan/MealPlanTabs";
import { MealTrackingDashboard } from "@/components/mealplan/MealTrackingDashboard";
import { ShareLinkCard } from "@/components/mealplan/ShareLinkCard";
import { MealPlanSettings } from "@/components/mealplan/MealPlanSettings";
import { MealPlanPDFContent } from "@/components/mealplan/MealPlanPDFContent";
import { useExportToPDF } from "@/hooks/useExportToPDF";

interface MealPlan {
  id: string;
  plan_name: string;
  status: string;
  start_date: string;
  end_date: string;
  total_daily_calories: number;
  ai_recommendations: any;
  tracking_link_id: string | null;
  patient: {
    id: string;
    full_name: string;
    avatar_url: string;
  };
}

interface TrackingLink {
  id: string;
  token: string;
  expires_at: string;
  last_accessed_at: string | null;
}

interface MealItem {
  id: string;
  day_number: number;
  meal_name: string;
  meal_type: string;
  scheduled_time: string;
  foods: any;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fat: number;
  ai_reasoning: string;
  display_order: number;
}

interface DayGroup {
  dayNumber: number;
  dayName: string;
  meals: MealItem[];
  totalCalories: number;
}

const dayNames: Record<number, string> = {
  1: "Segunda-feira",
  2: "Terça-feira",
  3: "Quarta-feira",
  4: "Quinta-feira",
  5: "Sexta-feira",
  6: "Sábado",
  7: "Domingo"
};

const mealTypeNames: Record<string, string> = {
  breakfast: "Café da manhã",
  morning_snack: "Lanche da manhã",
  lunch: "Almoço",
  afternoon_snack: "Lanche da tarde",
  dinner: "Jantar",
  evening_snack: "Ceia"
};

export default function MealPlanDetailView() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mealPlan, setMealPlan] = useState<MealPlan | null>(null);
  const [trackingLink, setTrackingLink] = useState<TrackingLink | null>(null);
  const [dayGroups, setDayGroups] = useState<DayGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const pdfRef = useRef<HTMLDivElement>(null);
  const { exportToPDF, isExporting } = useExportToPDF(pdfRef);

  const defaultTab = searchParams.get("tab") || "plan";

  useEffect(() => {
    loadMealPlanDetails();
  }, [planId]);

  const loadMealPlanDetails = async () => {
    try {
      setLoading(true);

      // Buscar plano alimentar
      const { data: planData, error: planError } = await supabase
        .from("meal_plans")
        .select(`
          *,
          patient:patients_onboarding(id, full_name, avatar_url)
        `)
        .eq("id", planId)
        .single();

      if (planError) throw planError;

      setMealPlan(planData);

      // Buscar tracking link se existir
      if (planData.tracking_link_id) {
        const { data: linkData } = await supabase
          .from("meal_tracking_links")
          .select("*")
          .eq("id", planData.tracking_link_id)
          .single();

        if (linkData) {
          setTrackingLink(linkData);
        }
      }

      // Buscar meal_items
      const { data: mealsData, error: mealsError } = await supabase
        .from("meal_items")
        .select("*")
        .eq("meal_plan_id", planId)
        .order("day_number", { ascending: true })
        .order("display_order", { ascending: true });

      if (mealsError) throw mealsError;

      // Agrupar por dia
      const grouped = groupMealsByDay(mealsData || []);
      setDayGroups(grouped);
    } catch (error) {
      console.error("Error loading meal plan:", error);
      toast.error("Erro ao carregar detalhes do plano alimentar");
    } finally {
      setLoading(false);
    }
  };

  const groupMealsByDay = (meals: MealItem[]): DayGroup[] => {
    const groups: Record<number, MealItem[]> = {};

    meals.forEach((meal) => {
      const dayNum = meal.day_number || 1;
      if (!groups[dayNum]) {
        groups[dayNum] = [];
      }
      groups[dayNum].push(meal);
    });

    return Object.entries(groups).map(([dayNum, meals]) => ({
      dayNumber: parseInt(dayNum),
      dayName: dayNames[parseInt(dayNum)] || `Dia ${dayNum}`,
      meals,
      totalCalories: meals.reduce((sum, meal) => sum + (meal.total_calories || 0), 0)
    })).sort((a, b) => a.dayNumber - b.dayNumber);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando plano alimentar...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!mealPlan) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Plano alimentar não encontrado</p>
            <div className="flex justify-center mt-4">
              <Button onClick={() => navigate("/app/aplicativo/mealplan")} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Renderizar conteúdo da Tab "Plano"
  const renderPlanTab = () => (
    <div className="space-y-6">
      {/* Days */}
      {dayGroups.map((dayGroup) => (
        <Card key={dayGroup.dayNumber}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl">{dayGroup.dayName}</CardTitle>
              <Badge variant="outline">{dayGroup.totalCalories} kcal</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {dayGroup.meals.map((meal) => (
              <div key={meal.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{mealTypeNames[meal.meal_type] || meal.meal_name}</h4>
                    {meal.scheduled_time && (
                      <p className="text-sm text-muted-foreground">{meal.scheduled_time}</p>
                    )}
                  </div>
                  <Badge variant="secondary">{meal.total_calories || 0} kcal</Badge>
                </div>

                {/* Foods list */}
                {meal.foods && Array.isArray(meal.foods) && meal.foods.length > 0 && (
                  <div className="space-y-1">
                    {meal.foods.map((food: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          • {food.name || food.food}
                        </span>
                        <span className="text-muted-foreground">
                          {food.quantity || food.amount} {food.unit || ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Macros */}
                {(meal.total_protein || meal.total_carbs || meal.total_fat) && (
                  <div className="flex gap-4 text-xs text-muted-foreground pt-2 border-t">
                    {meal.total_protein > 0 && <span>Proteínas: {meal.total_protein}g</span>}
                    {meal.total_carbs > 0 && <span>Carboidratos: {meal.total_carbs}g</span>}
                    {meal.total_fat > 0 && <span>Gorduras: {meal.total_fat}g</span>}
                  </div>
                )}

                {/* AI Reasoning */}
                {meal.ai_reasoning && (
                  <div className="text-xs text-muted-foreground italic pt-2 border-t">
                    💡 {meal.ai_reasoning}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {dayGroups.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Nenhuma refeição encontrada neste plano
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Renderizar conteúdo da Tab "Acompanhamento"
  const renderTrackingTab = () => (
    <div className="space-y-6">
      {trackingLink ? (
        <>
          <ShareLinkCard
            token={trackingLink.token}
            expiresAt={trackingLink.expires_at}
            lastAccessedAt={trackingLink.last_accessed_at}
            patientName={mealPlan?.patient?.full_name || ""}
          />
          <MealTrackingDashboard
            planId={planId!}
            totalDailyCalories={mealPlan?.total_daily_calories || 0}
          />
        </>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Este plano não possui um link de acompanhamento ativo.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Renderizar conteúdo da Tab "Configurações"
  const renderSettingsTab = () => (
    <MealPlanSettings
      planId={planId!}
      initialName={mealPlan?.plan_name || ""}
      initialStatus={mealPlan?.status || "active"}
      onUpdate={loadMealPlanDetails}
    />
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button onClick={() => navigate("/app/aplicativo/mealplan")} variant="outline" size="sm">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (mealPlan && dayGroups.length > 0) {
                const allMeals = dayGroups.flatMap(day => day.meals);
                exportToPDF(`plano-${mealPlan.plan_name || 'alimentar'}.pdf`);
              }
            }}
            disabled={isExporting}
          >
            <FileDown className="h-4 w-4 mr-2" />
            {isExporting ? "Gerando..." : "Exportar PDF"}
          </Button>
          <Badge variant={mealPlan?.status === "active" ? "default" : "secondary"}>
            {mealPlan?.status === "active" ? "Ativo" : "Inativo"}
          </Badge>
        </div>
      </div>

      {/* Plan Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1 flex-1">
              <CardTitle className="text-2xl">{mealPlan?.plan_name || "Plano Alimentar"}</CardTitle>
              <CardDescription className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {mealPlan?.patient?.full_name}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Período</p>
                <p className="text-muted-foreground">
                  {mealPlan?.start_date ? new Date(mealPlan.start_date).toLocaleDateString("pt-BR") : "—"} até{" "}
                  {mealPlan?.end_date ? new Date(mealPlan.end_date).toLocaleDateString("pt-BR") : "—"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <div className="text-sm">
                <p className="font-medium">Calorias diárias</p>
                <p className="text-muted-foreground">{mealPlan?.total_daily_calories || "—"} kcal</p>
              </div>
            </div>
          </div>

          {mealPlan?.ai_recommendations && typeof mealPlan.ai_recommendations === 'object' && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Recomendações da IA</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  {Array.isArray(mealPlan.ai_recommendations.recommendations) ? (
                    mealPlan.ai_recommendations.recommendations.map((rec: string, idx: number) => (
                      <p key={idx}>• {rec}</p>
                    ))
                  ) : typeof mealPlan.ai_recommendations === 'string' ? (
                    <p>{mealPlan.ai_recommendations}</p>
                  ) : (
                    <p>{JSON.stringify(mealPlan.ai_recommendations)}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <MealPlanTabs
        defaultTab={defaultTab}
        planTab={renderPlanTab()}
        trackingTab={renderTrackingTab()}
        settingsTab={renderSettingsTab()}
      />

      {/* Hidden PDF Content */}
      <div ref={pdfRef} className="hidden">
        {mealPlan && dayGroups.length > 0 && (
          <MealPlanPDFContent
            plan={{
              id: mealPlan.id,
              plan_name: mealPlan.plan_name,
              start_date: mealPlan.start_date,
              end_date: mealPlan.end_date,
              total_daily_calories: mealPlan.total_daily_calories,
              notes: null,
              created_at: new Date().toISOString(),
              patient: {
                full_name: mealPlan.patient.full_name,
                email: null,
              },
            }}
            mealItems={dayGroups.flatMap(day => day.meals)}
          />
        )}
      </div>
    </div>
  );
}
